import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Offcanvas, Dropdown } from 'bootstrap';
import Table from 'datatables.net-bs5';
import DataTable, { DataTableRef } from 'datatables.net-react';
import 'datatables.net-fixedcolumns-bs4';
import { ToastContainer } from './ToastContainer';
import { ToastMessage, ToastMessageType } from '../model/ToastMessage';
import { Node } from '../model/Node';
import { assignLicense, getAccounts, getLocations, getNodes, unassignLicense } from '../services/StudioService';
import { Instance } from '../model/Instance';
import { NodeDetails } from './NodeDetails';
import { DateTime } from 'luxon';
import { Spinner } from './Spinner';
import { Account } from '../model/Account';
import { Location } from '../model/Location';
import { CountBar } from './CountBar';
import { LicenseState } from '../model/LicenseState';
import { LicenseStateBadge } from './LicenseState';
DataTable.use(Table);

const Overview = ({ instance }: Props) => {
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [active, setActive] = useState(0);
  const [inactive, setInactive] = useState(0);
  const [accountsCount, setAccountsCount] = useState(0);
  const [locationsCount, setLocationsCount] = useState(0);
  const [assignedNodes, setAssignedNodes] = useState<Node[]>([]);
  const [toastMessages, setToastMessages] = useState<ToastMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [tableWidth, setTableWidth] = useState(0);
  const offCanvasRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<DataTableRef | null>(null);
  const selectedAccountIdRef = useRef('');
  const selectedLocationIdRef = useRef('');
  const selectedStatusFilterRef = useRef<StatusFilter>(StatusFilter.all);
  const selectedNodesRef = useRef<Map<string, Node>>(new Map());
  const selectAllRef = useRef(false);
  const instanceRef = useRef<Instance>(instance);
  const actionDropdownRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    offCanvasRef.current && Offcanvas.getOrCreateInstance(offCanvasRef.current, { backdrop: true });
    actionDropdownRef.current && new Dropdown(actionDropdownRef.current, { autoClose: true });
    containerRef.current && setTableWidth(containerRef.current.clientWidth);
  }, []);

  useEffect(() => {
    if(instance) {
      instanceRef.current = instance;
      loadAccounts();
      loadLocations();
      loadAssignedNodes();
      const dt = getDataTable();
      if(dt) {
        dt.ajax.reload();
        const onDraw = () => {
          if(selectAllRef.current) {
            const nodes = dt.rows().data().toArray() as Node[];
            for(const node of nodes) {
              if(!selectedNodesRef.current.has(node.id)) {
                selectedNodesRef.current.set(node.id, node);
              }
            }
          }
        };
        dt.on('draw', onDraw);
        return () => {
          dt.off('draw', onDraw);
        };
      }
    }
  }, [ instance ]);

  const onChangeSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const dt = getDataTable();
    dt && dt.search(event.target.value).draw();
  }

  const onChangeAccount = (value: string) => {
    selectedAccountIdRef.current = value;
    selectedLocationIdRef.current = '';
    selectAllRef.current = false;
    selectedNodesRef.current.clear();
    loadLocations(value);
    const dt = getDataTable();
    dt && dt.ajax.reload();
  }

  const onChangeLocation = (value: string) => {
    selectedLocationIdRef.current = value;
    selectAllRef.current = false;
    selectedNodesRef.current.clear();
    const dt = getDataTable();
    dt && dt.ajax.reload();
  }

  const onChangeStatusFilter = (event: ChangeEvent<HTMLSelectElement>) => {
    selectedStatusFilterRef.current = event.target.value as StatusFilter;
    const dt = getDataTable();
    dt && dt.ajax.reload();
  }

  const onSelect = (node: Node, select: boolean) => {
    if(select) {
      !selectedNodesRef.current.has(node.id) && selectedNodesRef.current.set(node.id, node);
    }
    else {
      selectedNodesRef.current.delete(node.id);
      selectAllRef.current = false;
    }
    const dt = getDataTable();
    dt && dt.draw(false);
  }

  const onSelectAll = (value: boolean) => {
    const dt = getDataTable();
    if(dt) {
      if(value) {
        const rows = dt.rows();
        const nodes = rows.data().toArray() as Node[];
        nodes.forEach(node => !selectedNodesRef.current.has(node.id) && selectedNodesRef.current.set(node.id, node));
      }
      else {
        console.log('deselect all');
        selectedNodesRef.current.clear();
      }
      selectAllRef.current = value;
      dt.draw(false);
    }
  }

  const onSelectNode = (node: Node) => {
    setSelectedNode(node);
    if(offCanvasRef.current) {
      const instance = Offcanvas.getInstance(offCanvasRef.current);
      instance && instance.show();
    }
  }

  const onExport = async () => {
    let nodes: Node[] = [];
    if(selectAllRef.current) {
      const dt = getDataTable();
      if(dt) {
        const order = dt.order();
        const search = dt.search();
        const result = await getNodes(instanceRef.current, 0, 10000, columns[order[0][0]].data, order[0][1] === 'asc', selectedAccountIdRef.current, selectedLocationIdRef.current, selectedStatusFilterRef.current, search ? search.toString() : '');
        nodes = result.nodes;
      }
    }
    else {
      nodes = Array.from(selectedNodesRef.current.values());
    }
    const csv = 'Node ID;Node Name;Location ID; Location Name;Installation ID;Installation Name;Account ID;Account Name;User;Activation Date;Renewal Date\n' + nodes.map(node => `${node.id};${node.name};${node.locationShortId};${node.locationName};${node.installationShortId};${node.installationName};${node.accountShortId};${node.accountName};${'User'};${node.activationDate ? DateTime.fromMillis(node.activationDate).toFormat('dd/MM/yyyy') : ''};${node.renewalDate ? DateTime.fromMillis(node.renewalDate).toFormat('dd/MM/yyyy') : ''}`).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'nodes.csv';
    link.click();
    URL.revokeObjectURL(url);
  }

  const onChangeLicenseState = async (value: LicenseState) => {
    let nodes: Node[] = [];
    if(selectAllRef.current) {
      const dt = getDataTable();
      if(dt) {
        const order = dt.order();
        const search = dt.search();
        const result = await getNodes(instanceRef.current, 0, 10000, columns[order[0][0]].data, order[0][1] === 'asc', selectedAccountIdRef.current, selectedLocationIdRef.current, selectedStatusFilterRef.current, search ? search.toString() : '');
        nodes = result.nodes;
      }
    }
    else {
      nodes = Array.from(selectedNodesRef.current.values());
    }
    try {
      for(const node of nodes) {
        if(value === LicenseState.licensed && node.licenseState === LicenseState.unlicensed) {
          await assignLicense(instanceRef.current, node.id);
          setToastMessages([ { type: ToastMessageType.success, message: 'Unlicensed nodes were licensed successfully', title: 'Success' } ]);
        }
        else if(!node.decommissioned && value) {
          await unassignLicense(instanceRef.current, node.id, node.licenseId);
          setToastMessages([ { type: ToastMessageType.success, message: 'Licensed nodes were unlicensed successfully', title: 'Success' } ]);
        }
        node.licenseState = value;
      }
      const dt = getDataTable();
      dt && dt.ajax.reload();
    }
    catch(error) {
      console.log(error);
      setToastMessages([ { type: ToastMessageType.error, message: 'An error occurred while updating nodes', title: 'Error' } ]);
    }
  }

  const onCloseOffCanvas = () => {
    setSelectedNode(null);
    if(offCanvasRef.current) {
      const instance = Offcanvas.getInstance(offCanvasRef.current);
      instance && instance.hide();
    }
  }

  const onReassignLicense = async (previousNode: Node) =>  {
    try {
      instanceRef.current && selectedNode && await assignLicense(instanceRef.current, selectedNode.id, previousNode.licenseId);
      await loadAssignedNodes();
      const dt = getDataTable();
      dt && dt.ajax.reload();
    }
    catch(error) {
      console.log(error);
      setToastMessages([ { type: ToastMessageType.error, message: 'An error occurred while reassigning node license', title: 'Error' } ]);
    }
  }

  const loadAccounts = async () => {
    setLoading(true);
    const accounts = await getAccounts(instanceRef.current);
    setAccounts(accounts);
    setLoading(false);
  }

  const loadLocations = async (accountId?: string) => {
    setLoading(true);
    const locations = await getLocations(instanceRef.current, accountId);
    setLocations(locations);
    setLoading(false);
  }

  const loadAssignedNodes = async () => {
    setLoading(true);
    const result = await getNodes(instanceRef.current, 0, Number.MAX_SAFE_INTEGER, 'name', true, '', '', StatusFilter.activated, '');
    setAssignedNodes(result.nodes);
    setLoading(false);
  }

  const shortenName = (name: string) => {
    return name.length > 56 ? name.substring(0, 53) + '...' : name;
  }

  const getDataTable = () => {
    if(tableRef.current) {
      return tableRef.current.dt();
    }
    return null;
  }

  const order: { name: string; dir: 'asc' | 'desc'; } = { name: 'name', dir: 'asc' };
  const styleType: 'single' | 'multi' = 'multi';
  const options = {
    language: {
      info: 'Showing page _PAGE_ of _PAGES_',
      infoEmpty: 'No nodes to display',
      infoFiltered: '(filtered from a total of _MAX_ nodes)',
      lengthMenu: '',
      zeroRecords: 'No node to display',
      search: '',
      searchPlaceholder: 'Search',
      select: {
        rows: {
          _: '%d selected nodes',
          1: '1 selected node'
        }
      }
    },
    layout: {
      topStart: null,
      topEnd: null,
      bottomStart: 'info',
      bottomEnd: 'paging'
    },
    select: {
      style: styleType,
      selector: 'td:first-child'
    },
    rowId: 'id',
    order: order,
    serverSide: true,
    scrollX: true,
    scrollCollapse: true,
    fixedColumns: {
      start: 1,
      end: 1
    }
  };
  const columns = [
    { data: 'id', width: '20px', searchable: false, orderable: false },
    { data: 'shortId', name: 'id', width: '50px', render: (shortId: string) => shortId || '' },
    { data: 'name', name: 'name', width: '274px', render: shortenName },
    { data: 'accountName', name: 'accountName', width: '274px', render: shortenName },
    { data: 'locationName', name: 'locationName', width: '274px', render: shortenName },
    { data: 'installationName', name: 'installationName', width: '274px', render: shortenName },
    { data: 'licenseState', name: 'licenseState' },
    { data: 'activationDate', name: 'activationDate', render: (timestamp: number, type: string) => type === 'sort' ? timestamp || 0 : (timestamp ? DateTime.fromMillis(timestamp).toFormat('dd/MM/yyyy') : '-') },
    { data: 'renewalDate', name: 'renewalDate', render: (timestamp: number, type: string) => type === 'sort' ? timestamp || 0 : (timestamp ? DateTime.fromMillis(timestamp).toFormat('dd/MM/yyyy') : '-') },
    { data: 'id', width: '40px', searchable: false, orderable: false }
  ];
  const slots = {
    0: (id: string, node: Node) => {
      return (
        <input type="checkbox" className="dt-select-checkbox" checked={selectedNodesRef.current.has(id) || selectAllRef.current} onChange={e => onSelect(node, e.target.checked)} />
      );
    },
    6: (licenseState: string, node: Node) => {
      return (
        <LicenseStateBadge state={licenseState as LicenseState} />
      );
    },
    9: (id: string, node: Node) => {
      return (
        <div className="d-flex">
          <a data-testid="view-node-button" className="text-dark" onClick={() => onSelectNode(node)}>
            <i className="icon-base ti ti-eye me-1"></i>
          </a>
        </div>
      );
    }
  };
  const ajax = async (data: any, callback: any) => {
    setLoading(true);
    let orderBy = '';
    let orderAsc = true;
    if(data.order.length) {
      orderBy = data.order[0].name;
      orderAsc = data.order[0].dir === 'asc';
    }
    console.log('ajax', instanceRef.current.name);
    const result = await getNodes(instanceRef.current, data.start, data.length, orderBy, orderAsc, selectedAccountIdRef.current, selectedLocationIdRef.current, selectedStatusFilterRef.current, data.search.value);
    console.log(result.nodes);
    callback({ data: result.nodes, recordsTotal: result.total, recordsFiltered: result.filtered });
    setActive(result.active);
    setInactive(result.inactive);
    setAccountsCount(result.accounts);
    setLocationsCount(result.locations);
    setLoading(false);
  };

  return (
    <>
      <div className="mb-4 mt-4">
        <CountBar activeNodes={active} inactiveNodes={inactive} accounts={accountsCount} locations={locationsCount} />
      </div>
      <div className="d-flex justify-content-between mt-4 pt-4 border-top">
        <div className="me-2" style={{ width: 130 }}>
          {selectAllRef.current || selectedNodesRef.current.size > 0 && <div ref={actionDropdownRef} className="btn-action">
            <button className="btn btn-xs dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false" data-bs-auto-close="true">Action&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</button>
            <ul className="dropdown-menu">
              <li>
                <a className="dropdown-item text-danger" href="#" onClick={() => onChangeLicenseState(LicenseState.unlicensed)}>
                  <i className="icon-base ti ti-x me-1"></i>
                  <span>Unassign license</span>
                </a>
              </li>
              <li>
                <a className="dropdown-item text-success" href="#" onClick={() => onChangeLicenseState(LicenseState.licensed)}>
                  <i className="icon-base ti ti-plus me-1"></i>
                  <span>Assign license</span>
                </a>
              </li>
              <li>
                <a className="dropdown-item" href="#" onClick={onExport}>
                  <i className="icon-base ti ti-file-export me-1"></i>
                  <span>Export</span>
                </a>
              </li>
            </ul>
          </div>}
        </div>
        <div className="nodes-filter d-flex flex-wrap justify-content-end">
          <select data-testid="account-select" className="form-select form-select-sm mb-2" value={selectedAccountIdRef.current} onChange={(event) => onChangeAccount(event.target.value)}>
            <option value="">Account...</option>
            {
              accounts.map(a =>
                <option key={a.id} value={a.id}>{a.name}</option>
              )
            }
          </select>
          <select data-testid="location-select" className="form-select form-select-sm ms-2 mb-2" value={selectedLocationIdRef.current} onChange={(event) => onChangeLocation(event.target.value)}>
            <option value="">Location...</option>
            {
              locations.map(l =>
                <option key={l.id} value={l.id}>{l.name}</option>
              )
            }
          </select>
          <select className="form-select form-select-sm w-auto text-capitalize ms-2 mb-2" onChange={onChangeStatusFilter}>
            {
              Object.values(StatusFilter).map(status =>
                <option key={status} value={status}>{status}</option>
              )
            }
          </select>
          <input data-testid="nodes-search" type="text" className="form-control form-control-sm ms-2 mb-2" placeholder="Search" onChange={onChangeSearch} />
          {/* <button className="btn btn-sm btn-primary" disabled={!(selectAllRef.current || selectedNodesRef.current.size > 0)} onClick={onExport}>Export Selected</button> */}
        </div>
      </div>
      <div ref={containerRef} className="w-100">
        <div style={{ width: tableWidth, maxWidth: tableWidth }}>
          {instance && <DataTable ref={tableRef} className="table border-top mt-3 w-100 bg-white" ajax={ajax} options={options} columns={columns} slots={slots} data-testid="instance-nodes-table">
            <thead>
              <tr>
                <th>
                  <input type="checkbox" className="dt-select-checkbox" onChange={(e) => onSelectAll(e.target.checked)} checked={selectAllRef.current} />
                </th>
                <th>ID</th>
                <th>NAME</th>
                <th>ACCOUNT</th>
                <th>LOCATION</th>
                <th>INSTALLATION</th>
                <th>LICENSE STATE</th>
                <th>ACTIVATED ON</th>
                <th>RENEWS ON</th>
                <th>ACTION</th>
              </tr>
            </thead>
          </DataTable>}
        </div>
      </div>
      <div ref={offCanvasRef} className="offcanvas offcanvas-end" tabIndex={-1} id="offcanvas">
        <div className="offcanvas-header">
          <h5 className="offcanvas-title">Node Details</h5>
          <button type="button" className="btn-close bg-white text-reset" data-bs-dismiss="offcanvas" aria-label="Close"></button>
        </div>
        <div className="offcanvas-body mx-0 h-100 flex-grow-0">
          {selectedNode && <NodeDetails node={selectedNode} assignedNodes={assignedNodes} onReassignLicense={onReassignLicense} onClose={onCloseOffCanvas} />}
        </div>
      </div>
      <ToastContainer messages={toastMessages} />
      <Spinner loading={loading} />
    </>
  );
}

interface Props {
  instance: Instance
}

enum StatusFilter {
  all = 'all',
  activated = 'activated',
  pending = 'pending to activate',
  unlicensed = 'unlicensed'
}

export { Overview };
